import React from 'react';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';

export type HeroVariant = 'home' | 'rewards';

export interface MobileHeroProps {
  variant?: HeroVariant;
  compact?: boolean;
  showProgressRing?: boolean;
  progressRingProps?: {
    currentValue: number;
    maxValue: number;
    label: string;
    subLabel?: React.ReactNode;
    onClick?: () => void;
  };
  showStats?: boolean;
  stats?: Array<{
    label?: string;
    value: string | number;
    unit?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    isClickable?: boolean;
    customLabel?: React.ReactNode;
  }>;
  showRewardInfo?: boolean;
  rewardInfo?: {
    currencySymbol: string;
    amount: number;
    name: string;
    onClick?: () => void;
  };
  // Home variant specific props
  homeProgressRingProps?: {
    currentValue: number;
    maxValue: number;
    label: string;
    subLabel?: React.ReactNode;
    onClick?: () => void;
  };
  // Rewards variant specific props
  rewardsProgressRingProps?: {
    currentValue: number;
    maxValue: number;
    label: string;
    subLabel?: React.ReactNode;
    onClick?: () => void;
  };
  // Reward progress ring props
  rewardProgressRingProps?: {
    currentValue: number;
    maxValue: number;
    label: string;
    subLabel?: React.ReactNode;
    onClick?: () => void;
  };
}

const MobileHero: React.FC<MobileHeroProps> = ({
  variant = 'home',
  compact = false,
  showProgressRing = false,
  progressRingProps,
  showStats = false,
  stats = [],
  showRewardInfo = false,
  rewardInfo,
  homeProgressRingProps,
  rewardsProgressRingProps,
  rewardProgressRingProps,
}) => {
  const t = useTranslationByKey('COMPONENTS.LEVEL_CONTAINER');
  const { formatCurrency } = useUserCurrency();

  // Determine which progress ring props to use based on variant
  const effectiveProgressRingProps =
    variant === 'rewards' ? rewardsProgressRingProps || progressRingProps : homeProgressRingProps || progressRingProps;

  const getProgressPercent = (currentValue: number, maxValue: number) => {
    if (maxValue <= 0) return 0;
    return Math.min(100, Math.max(0, (currentValue / maxValue) * 100));
  };

  const renderCompactProgressTile = ({
    title,
    label,
    subLabel,
    currentValue,
    maxValue,
    onClick,
    barClassName,
  }: {
    title: string;
    label: React.ReactNode;
    subLabel?: React.ReactNode;
    currentValue: number;
    maxValue: number;
    onClick?: () => void;
    barClassName: string;
  }) => {
    const content = (
      <>
        <div className="flex min-w-0 items-center justify-between gap-2">
          <span className="truncate text-[10px] font-semibold uppercase leading-3 opacity-75">{title}</span>
          <span className="inline-flex shrink-0 items-center gap-1 font-spaceGrotesk text-sm font-bold leading-4">
            {label}
            {subLabel &&
              (typeof subLabel === 'string' ? (
                <span className="text-[10px] font-semibold leading-3 opacity-80">{subLabel}</span>
              ) : (
                <span className="inline-flex h-3.5 w-3.5 items-center justify-center">{subLabel}</span>
              ))}
          </span>
        </div>
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/20">
          <div
            className={`h-full rounded-full ${barClassName}`}
            style={{ width: `${getProgressPercent(currentValue, maxValue)}%` }}
          />
        </div>
        <div className="mt-1 truncate text-[10px] font-medium leading-3 opacity-85">
          {currentValue.toLocaleString()} / {maxValue.toLocaleString()}
        </div>
      </>
    );
    const className =
      'min-w-0 rounded-xl bg-white/15 px-2.5 py-2 text-left ring-1 ring-white/25 transition-all duration-300';

    return onClick ? (
      <button type="button" className={`${className} hover:bg-white/20`} onClick={onClick}>
        {content}
      </button>
    ) : (
      <div className={className}>{content}</div>
    );
  };

  if (compact) {
    const compactProgressTileCount =
      (showProgressRing && effectiveProgressRingProps ? 1 : 0) + (rewardProgressRingProps ? 1 : 0);
    const compactProgressGridMaxClassName = compactProgressTileCount > 1 ? 'md:max-w-[24rem]' : 'md:max-w-[12rem]';

    return (
      <div
        className="relative isolate w-full overflow-hidden bg-gradient-to-tr from-indigo-600 via-violet-500 to-purple-600 px-3 pb-5 pt-3 text-white shadow-sm ring-1 ring-black/10"
        style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 20px), 0% 100%)' }}
      >
        <div className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-black/10 blur-2xl" />

        <div className="relative z-10 flex min-w-0 items-start gap-2 md:justify-between">
          {(showProgressRing && effectiveProgressRingProps) || rewardProgressRingProps ? (
            <div
              className={`grid min-w-0 flex-1 gap-2 ${compactProgressGridMaxClassName} ${
                compactProgressTileCount > 1 ? 'grid-cols-2' : 'grid-cols-1'
              }`}
            >
              {showProgressRing &&
                effectiveProgressRingProps &&
                renderCompactProgressTile({
                  title: 'Level',
                  label: effectiveProgressRingProps.label,
                  subLabel: effectiveProgressRingProps.subLabel,
                  currentValue: effectiveProgressRingProps.currentValue,
                  maxValue: effectiveProgressRingProps.maxValue,
                  onClick: effectiveProgressRingProps.onClick,
                  barClassName: 'bg-white/90',
                })}
              {rewardProgressRingProps &&
                renderCompactProgressTile({
                  title: 'Reward',
                  label: rewardProgressRingProps.label,
                  subLabel: rewardProgressRingProps.subLabel,
                  currentValue: rewardProgressRingProps.currentValue,
                  maxValue: rewardProgressRingProps.maxValue,
                  onClick: rewardProgressRingProps.onClick,
                  barClassName: 'bg-gradient-to-r from-yellow-200 to-amber-300',
                })}
            </div>
          ) : null}

          {showStats && stats.length > 0 && (
            <div className="flex max-w-[40%] shrink-0 flex-col items-end gap-1">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`max-w-full rounded-lg bg-white/15 px-2 py-1 text-[11px] leading-4 ring-1 ring-white/25 ${
                    stat.isClickable ? 'cursor-pointer transition-all duration-300 hover:bg-white/20' : ''
                  }`}
                  onClick={stat.onClick || undefined}
                >
                  {stat.customLabel ? (
                    <span className="block truncate">{stat.customLabel}</span>
                  ) : (
                    <span className="inline-flex min-w-0 items-center gap-1">
                      {stat.icon && <span className="inline-flex h-3.5 w-3.5 shrink-0">{stat.icon}</span>}
                      <span className="truncate">
                        {stat.value} {stat.unit || ''}
                      </span>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {showRewardInfo && (
          <div className="relative z-10 mt-2 flex justify-center">
            {rewardInfo ? (
              <div
                className={`rounded-lg bg-white/15 px-2.5 py-1 text-xs ring-1 ring-white/25 ${
                  rewardInfo.onClick ? 'cursor-pointer transition-all duration-300 hover:bg-white/20' : ''
                }`}
                onClick={rewardInfo.onClick}
              >
                {formatCurrency(rewardInfo.amount)} {rewardInfo.name}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-white/40 bg-white/15 px-2.5 py-1 text-xs text-white/70 ring-1 ring-white/25">
                {t('SELECT_REWARD')}
              </div>
            )}
          </div>
        )}

        <svg className="absolute bottom-0 left-0 w-full" height="20" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path
            fill="rgba(255,255,255,0.15)"
            d="M0,224L48,202.7C96,181,192,139,288,106.7C384,75,480,53,576,80C672,107,768,181,864,213.3C960,245,1056,235,1152,208C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className="relative isolate overflow-hidden px-4 pt-4 pb-8 text-white shadow-sm ring-1 ring-black/10 w-full bg-gradient-to-tr from-indigo-600 via-violet-500 to-purple-600"
      style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 32px), 0% 100%)' }}
    >
      {/* Background blur effects - consistent with DesktopHero */}
      <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-14 -left-14 h-44 w-44 rounded-full bg-black/10 blur-2xl" />

      {/* Combined layout for progress and stats on mobile */}
      <div className="mt-2 flex items-center justify-between gap-3">
        {/* Progress Rings Container */}
        {(showProgressRing && effectiveProgressRingProps) || rewardProgressRingProps ? (
          <div className="flex justify-start gap-3">
            {/* Level Progress Ring */}
            {showProgressRing && effectiveProgressRingProps && (
              <div className="flex items-center gap-2">
                <div
                  className={`relative flex items-center justify-center transition-all duration-300 ${
                    effectiveProgressRingProps.onClick ? 'cursor-pointer hover:scale-105' : ''
                  }`}
                  onClick={effectiveProgressRingProps.onClick}
                >
                  <svg width="84" height="84" viewBox="0 0 116 116" className="transform -rotate-90">
                    <circle cx="58" cy="58" r="42" stroke="white" strokeWidth="10" className="opacity-20" fill="none" />
                    <circle
                      cx="58"
                      cy="58"
                      r="42"
                      stroke="url(#gradMobile)"
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 42}
                      strokeDashoffset={
                        2 *
                          Math.PI *
                          42 *
                          (1 - effectiveProgressRingProps.currentValue / effectiveProgressRingProps.maxValue) +
                        (effectiveProgressRingProps.currentValue / effectiveProgressRingProps.maxValue >= 0.99 ? 8 : 0)
                      }
                      fill="none"
                    />
                    <defs>
                      <linearGradient id="gradMobile" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#C7F0FF" stopOpacity="0.9" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute text-center">
                    <div className="text-lg font-semibold leading-none">{effectiveProgressRingProps.label}</div>
                    {effectiveProgressRingProps.subLabel && (
                      <div className="text-[9px] opacity-80 leading-none mt-1">
                        {effectiveProgressRingProps.subLabel}
                      </div>
                    )}
                  </div>
                  {showRewardInfo && rewardInfo && (
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-center">
                      <div
                        className={`text-[9px] opacity-90 leading-none whitespace-nowrap ${
                          rewardInfo.onClick ? 'cursor-pointer transition-opacity duration-300 hover:opacity-100' : ''
                        }`}
                        onClick={(event) => {
                          if (!rewardInfo.onClick) {
                            return;
                          }
                          event.stopPropagation();
                          rewardInfo.onClick();
                        }}
                      >
                        {formatCurrency(rewardInfo.amount)} {rewardInfo.name}
                      </div>
                    </div>
                  )}
                </div>

                {typeof effectiveProgressRingProps.currentValue === 'number' &&
                  typeof effectiveProgressRingProps.maxValue === 'number' && (
                    <div className="flex flex-col leading-tight text-xs text-right">
                      <span className="font-semibold">
                        {effectiveProgressRingProps.currentValue} / {effectiveProgressRingProps.maxValue}{' '}
                        {t('HERO_PROGRESS_UNIT')}
                      </span>
                      <span className="opacity-90">
                        {Math.max(effectiveProgressRingProps.maxValue - effectiveProgressRingProps.currentValue, 0)}{' '}
                        {t('HERO_NEXT_LEVEL_LABEL')}
                      </span>
                    </div>
                  )}
              </div>
            )}

            {/* Reward Progress Ring */}
            {rewardProgressRingProps && (
              <div
                className={`relative flex items-center justify-center transition-all duration-300 ${
                  rewardProgressRingProps.onClick ? 'cursor-pointer hover:scale-105' : ''
                }`}
                onClick={rewardProgressRingProps.onClick}
              >
                <svg width="84" height="84" viewBox="0 0 116 116" className="transform -rotate-90">
                  <circle cx="58" cy="58" r="42" stroke="white" strokeWidth="10" className="opacity-20" fill="none" />
                  <circle
                    cx="58"
                    cy="58"
                    r="42"
                    stroke="url(#rewardGradMobile)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 42}
                    strokeDashoffset={
                      2 * Math.PI * 42 * (1 - rewardProgressRingProps.currentValue / rewardProgressRingProps.maxValue) +
                      (rewardProgressRingProps.currentValue / rewardProgressRingProps.maxValue >= 0.99 ? 8 : 0)
                    }
                    fill="none"
                  />
                  <defs>
                    <linearGradient id="rewardGradMobile" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FFD700" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="#FFA500" stopOpacity="0.9" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute text-center">
                  <div className="text-lg font-semibold leading-none">{rewardProgressRingProps.label}</div>
                  {rewardProgressRingProps.subLabel && (
                    <div className="text-[9px] opacity-80 leading-none mt-1">{rewardProgressRingProps.subLabel}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Stats chips aligned to the right of the rings */}
        {showStats && stats.length > 0 && (
          <div className="flex flex-col items-end gap-1.5">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`rounded-xl bg-white/15 px-2.5 py-0.5 ring-1 ring-white/25 text-xs ${
                  stat.isClickable ? 'cursor-pointer hover:bg-white/20 transition-all duration-300' : ''
                }`}
                onClick={stat.onClick || undefined}
              >
                {stat.customLabel || (
                  <span className="inline-flex items-center gap-1">
                    {stat.icon && <span className="inline-flex h-4 w-4 shrink-0">{stat.icon}</span>}
                    <span>
                      {stat.value} {stat.unit || ''}
                    </span>
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reward Info Section */}
      {showRewardInfo && (
        <div className="mt-3 flex justify-center">
          {rewardInfo ? (
            <div
              className={`rounded-xl bg-white/15 px-3 py-1 ring-1 ring-white/25 text-sm ${
                rewardInfo.onClick ? 'cursor-pointer transition-all duration-300 hover:bg-white/20' : ''
              }`}
              onClick={rewardInfo.onClick}
            >
              {formatCurrency(rewardInfo.amount)} {rewardInfo.name}
            </div>
          ) : (
            <div className="rounded-xl bg-white/15 px-3 py-1 ring-1 ring-white/25 text-sm border-2 border-dashed border-white/40 text-white/60">
              {t('SELECT_REWARD')}
            </div>
          )}
        </div>
      )}

      {/* Wave bottom divider - consistent with DesktopHero */}
      <svg className="absolute bottom-0 left-0 w-full" height="32" viewBox="0 0 1440 320" preserveAspectRatio="none">
        <path
          fill="rgba(255,255,255,0.15)"
          d="M0,224L48,202.7C96,181,192,139,288,106.7C384,75,480,53,576,80C672,107,768,181,864,213.3C960,245,1056,235,1152,208C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        />
      </svg>
    </div>
  );
};

export default MobileHero;
